<template>
  <div class="support-ticket" @keydown.esc="guardEscape">
    <header class="support-ticket-header">
      <button type="button" class="ticket-icon-button" :disabled="sending" aria-label="Zurück zur Supportauswahl" @click="$emit('back')">
        <ChevronLeftIcon aria-hidden="true" />
      </button>
      <div class="ticket-team" aria-hidden="true">
        <img src="/assets/images/support-linda.jpeg" alt="" />
        <img src="/assets/images/support-david.jpg" alt="" />
      </div>
      <div class="ticket-heading">
        <h2>Ticket eröffnen</h2>
        <p>Unser Team meldet sich innerhalb eines Werktags</p>
      </div>
      <button type="button" class="ticket-icon-button" :disabled="sending" :aria-label="$t('message.close')" @click="$emit('close')">
        <XMarkIcon aria-hidden="true" />
      </button>
    </header>

    <div v-if="sent" class="ticket-success" role="status" tabindex="-1" ref="successMessage">
      <h3>Dein Ticket wurde gesendet.</h3>
      <p>Unser Team meldet sich innerhalb eines Werktags.</p>
    </div>
    <form v-else class="ticket-form" :aria-busy="sending" @submit.prevent="sendTicket">
      <textarea
        ref="messageInput"
        :value="modelValue"
        :disabled="sending"
        aria-label="Dein Anliegen"
        placeholder="Beschreibe dein Anliegen so genau wie möglich…"
        @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
      />

      <ul v-if="attachments.length" class="ticket-attachments" aria-label="Anhänge">
        <li v-for="(file, index) in attachments" :key="index">
          <PaperClipIcon aria-hidden="true" />
          <span :title="file.name">{{ file.name }}</span>
          <button type="button" class="ticket-remove" :disabled="sending" :aria-label="`${file.name} entfernen`" @click="removeAttachment(index)">
            <XMarkIcon aria-hidden="true" />
          </button>
        </li>
      </ul>

      <p v-if="errorMessage" class="ticket-error" role="alert">{{ errorMessage }}</p>
      <p v-if="sending" class="ticket-progress" role="status">{{ progressMessage }}</p>
      <div class="ticket-actions">
        <input ref="fileInput" type="file" multiple class="hidden" :disabled="sending" @change="addAttachments" />
        <div class="ticket-tools">
          <button type="button" class="ticket-attach" :disabled="sending" @click="fileInput?.click()">
            <PaperClipIcon aria-hidden="true" />
            Datei anhängen
          </button>
          <div ref="emojiControl" class="ticket-emoji-control" @keydown.esc.stop.prevent="closeEmojiPicker">
            <button
              ref="emojiButton"
              type="button"
              class="ticket-icon-button ticket-emoji-button"
              aria-label="Emoji-Auswahl"
              title="Emoji-Auswahl"
              aria-haspopup="dialog"
              :aria-expanded="showEmojiPicker"
              :disabled="sending"
              @click="toggleEmojiPicker"
            >
              <FaceSmileIcon aria-hidden="true" />
            </button>
            <SupportEmojiPicker
              v-if="showEmojiPicker"
              class="ticket-emoji-popup"
              @select="insertEmoji"
              @close="closeEmojiPicker"
            />
          </div>
        </div>
        <button type="submit" class="ticket-send" :disabled="sending || !modelValue.trim()">
          {{ sending ? 'Wird gesendet…' : 'Senden' }}
          <ArrowUpIcon aria-hidden="true" />
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { ArrowUpIcon, ChevronLeftIcon, FaceSmileIcon, PaperClipIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import SupportEmojiPicker from './SupportEmojiPicker.vue';
import { isAxiosError } from 'axios';
import supportTicketService, { type SupportErrorResponse } from '@/services/support-tickets';

const props = defineProps<{
  modelValue: string;
  attachments: File[];
}>();
const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void;
  (event: 'update:attachments', value: File[]): void;
  (event: 'back' | 'close'): void;
}>();

const messageInput = ref<HTMLTextAreaElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const emojiControl = ref<HTMLElement | null>(null);
const emojiButton = ref<HTMLButtonElement | null>(null);
const showEmojiPicker = ref(false);
const sending = ref(false);
const sent = ref(false);
const errorMessage = ref('');
const progressMessage = ref('');
const successMessage = ref<HTMLElement | null>(null);
let selectionStart = 0;
let selectionEnd = 0;

function guardEscape(event: KeyboardEvent) {
  if (sending.value) {
    event.stopPropagation();
    event.preventDefault();
  }
}

function validateAttachments(files: File[]): string {
  if (files.length > 10) return 'Du kannst maximal 10 Dateien anhängen.';
  const empty = files.find(file => file.size === 0);
  if (empty) return `„${empty.name}“ ist leer. Bitte wähle eine andere Datei.`;
  const oversized = files.find(file => file.size >= 5_242_880);
  if (oversized) return `„${oversized.name}“ muss kleiner als 5 MB sein.`;
  return '';
}

function describeError(error: unknown): string {
  if (isAxiosError<SupportErrorResponse>(error)) {
    const data = error.response?.data;
    if (error.response?.status === 400 && data) {
      const messages = [data.file, data.message, data.attachment_urls]
        .flatMap(field => Array.isArray(field) ? field : Object.values(field || {}).flat());
      if (messages.length) return messages.join(' ');
    }
    if (error.response?.status === 403) return 'Bitte melde dich erneut an, um dein Ticket zu senden.';
    if (data?.detail) return data.detail;
  }
  return 'Das Ticket konnte nicht gesendet werden. Bitte versuche es erneut. Dein Entwurf bleibt erhalten.';
}

async function sendTicket() {
  if (sending.value || sent.value) return;
  const message = props.modelValue.trim();
  const files = [...props.attachments];
  errorMessage.value = !message ? 'Bitte beschreibe dein Anliegen.' : validateAttachments(files);
  if (errorMessage.value) return;

  sending.value = true;
  showEmojiPicker.value = false;
  try {
    // Upload anew on every attempt so retries never reuse expired signed URLs.
    const attachmentUrls: string[] = [];
    for (const [index, file] of files.entries()) {
      progressMessage.value = `Datei ${index + 1} von ${files.length} wird hochgeladen…`;
      const response = await supportTicketService.uploadAttachment(file);
      if (!response?.data?.url) throw new Error('Missing attachment URL');
      attachmentUrls.push(response.data.url);
    }
    progressMessage.value = 'Ticket wird gesendet…';
    const response = await supportTicketService.createConversation({ message, attachment_urls: attachmentUrls });
    if (response?.status !== 201) throw new Error('Ticket creation was not confirmed');
    emit('update:modelValue', '');
    emit('update:attachments', []);
    sent.value = true;
    await nextTick();
    successMessage.value?.focus({ preventScroll: true });
  } catch (error) {
    errorMessage.value = describeError(error);
  } finally {
    sending.value = false;
    progressMessage.value = '';
  }
}

function toggleEmojiPicker() {
  if (showEmojiPicker.value) {
    closeEmojiPicker();
    return;
  }
  selectionStart = messageInput.value?.selectionStart ?? props.modelValue.length;
  selectionEnd = messageInput.value?.selectionEnd ?? selectionStart;
  showEmojiPicker.value = true;
}

function closeEmojiPicker() {
  showEmojiPicker.value = false;
  emojiButton.value?.focus({ preventScroll: true });
}

async function insertEmoji(emoji: string) {
  const message = props.modelValue.slice(0, selectionStart) + emoji + props.modelValue.slice(selectionEnd);
  emit('update:modelValue', message);
  showEmojiPicker.value = false;
  await nextTick();
  const caret = selectionStart + emoji.length;
  messageInput.value?.focus({ preventScroll: true });
  messageInput.value?.setSelectionRange(caret, caret);
}

function onOutsidePointerDown(event: PointerEvent) {
  if (showEmojiPicker.value && event.target instanceof Node && !emojiControl.value?.contains(event.target)) {
    showEmojiPicker.value = false;
  }
}

function addAttachments(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = [...props.attachments, ...Array.from(input.files || [])];
  errorMessage.value = validateAttachments(files);
  if (!errorMessage.value) emit('update:attachments', files);
  input.value = '';
}

function removeAttachment(index: number) {
  emit('update:attachments', props.attachments.filter((_, fileIndex) => fileIndex !== index));
  errorMessage.value = '';
}

onMounted(() => {
  messageInput.value?.focus({ preventScroll: true });
  document.addEventListener('pointerdown', onOutsidePointerDown);
});
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutsidePointerDown));
</script>

<style scoped>
.support-ticket {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: #14161a;
  font: 14px / 21px system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.support-ticket-header {
  display: flex;
  align-items: center;
  flex: none;
  gap: 12px;
  padding: 16px 16px 14px 12px;
  border-bottom: 1px solid #ededed;
}

.ticket-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #14161a;
}

.ticket-icon-button svg,
.ticket-attach svg {
  width: 20px;
  height: 20px;
  flex: none;
}

.ticket-team {
  display: flex;
  flex: none;
}

.ticket-team img {
  width: 40px;
  height: 40px;
  border: 2px solid #fff;
  border-radius: 50%;
  object-fit: cover;
}

.ticket-team img + img {
  margin-left: -12px;
}

.ticket-heading {
  flex: 1;
  min-width: 0;
}

.ticket-heading h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.ticket-heading p {
  margin: 2px 0 0;
  color: #6c6f74;
}

.ticket-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.ticket-form textarea {
  flex: 1;
  width: 100%;
  min-height: 60px;
  padding: 24px 24px 12px;
  resize: none;
  border: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: #14161a;
  font: inherit;
  font-size: 16px;
  line-height: 24px;
}

.ticket-form textarea::placeholder {
  color: #6c6f74;
  opacity: 1;
}

.ticket-success {
  padding: 24px;
  outline: none;
}

.ticket-success h3 {
  font-weight: 600;
}

.ticket-success p,
.ticket-progress {
  color: #6c6f74;
}

.ticket-error,
.ticket-progress {
  margin: 8px 20px;
}

.ticket-error {
  color: #b42318;
}

.ticket-attachments {
  flex: none;
  max-height: 120px;
  overflow-y: auto;
  margin: 0;
  padding: 0 20px;
  list-style: none;
}

.ticket-attachments li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f4f4f4;
}

.ticket-attachments span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-attachments svg,
.ticket-send svg {
  flex: none;
  width: 16px;
  height: 16px;
}

.ticket-remove {
  padding: 4px;
  border-radius: 4px;
}

.ticket-actions {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  gap: 8px;
  padding: 12px 20px 20px 8px;
}

.ticket-tools,
.ticket-emoji-control {
  display: flex;
  align-items: center;
}

.ticket-emoji-button {
  color: #6c6f74;
}

.ticket-emoji-button[aria-expanded='true'] {
  background: #f4f4f4;
  color: #df4d44;
}

.ticket-emoji-popup {
  position: absolute;
  right: 16px;
  bottom: calc(100% + 4px);
  left: 16px;
  z-index: 1;
}

.ticket-attach,
.ticket-send {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font: inherit;
  white-space: nowrap;
}

.ticket-attach {
  padding: 8px 16px;
  border-radius: 9999px;
  font-weight: 500;
}

.ticket-send {
  padding: 10px 18px;
  border-radius: 9999px;
  background: #df4d44;
  color: #fff;
  font-weight: 600;
  transition: background 200ms, opacity 200ms;
}

button {
  cursor: pointer;
}

button:focus-visible {
  outline: 2px solid #df4d44;
  outline-offset: 2px;
}

.ticket-icon-button:hover,
.ticket-attach:hover,
.ticket-remove:hover {
  background: #f4f4f4;
}

.ticket-send:hover:not(:disabled) {
  background: #cc4840;
}

button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 380px) {
  .support-ticket-header {
    gap: 8px;
    padding-left: 8px;
    padding-right: 8px;
  }

  .ticket-actions {
    flex-wrap: wrap;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ticket-send {
    transition: none;
  }
}
</style>
